import React, { useState, useRef, useEffect } from 'react';
import './ProductTable.css';

/* ── Mini dropdown que sale al hacer click en el encabezado ── */
const ColMenu = ({ children, label, sortField, field, sortDir, onSort, active, toggleMode }) => {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const ref = useRef(null);

    useEffect(() => {
        if (toggleMode) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const closeOnScroll = () => setOpen(false);
        document.addEventListener('mousedown', handler);
        window.addEventListener('scroll', closeOnScroll, true);
        return () => {
            document.removeEventListener('mousedown', handler);
            window.removeEventListener('scroll', closeOnScroll, true);
        };
    }, [toggleMode]);

    const handleOpen = () => {
        if (toggleMode) {
            if (!active) {
                onSort(field, 'asc');
            } else {
                onSort(field, sortDir === 'asc' ? 'desc' : 'asc');
            }
            return;
        }
        if (!open && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setMenuPos({ top: rect.bottom, left: rect.left });
        }
        setOpen(o => !o);
    };

    const arrow = sortField === field
        ? (sortDir === 'asc' ? ' ▴' : ' ▾')
        : (toggleMode ? ' ⇅' : ' ⇅');

    return (
        <th ref={ref} className="pt-th" style={{ cursor: 'pointer', userSelect: 'none' }}>
            <span
                onClick={handleOpen}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    color: active ? '#3b82f6' : 'inherit'
                }}
            >
                {label}
                <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{arrow}</span>
                {active && <span style={{ fontSize: '0.6rem', background: '#3b82f6', color: '#fff', borderRadius: 4, padding: '1px 4px' }}>●</span>}
            </span>

            {!toggleMode && open && (
                <div style={{
                    position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999,
                    background: 'var(--bg-card, #1e2a3a)', border: '1px solid var(--border-color, #334155)',
                    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    minWidth: 180, padding: '6px 0', fontSize: '0.85rem'
                }}>
                    {children}
                </div>
            )}
        </th>
    );
};

const MenuItem = ({ onClick, children, selected }) => (
    <div
        onClick={onClick}
        style={{
            padding: '8px 14px', cursor: 'pointer',
            background: selected ? 'rgba(59,130,246,0.15)' : 'transparent',
            color: selected ? '#3b82f6' : 'var(--text-primary, #e2e8f0)',
            display: 'flex', alignItems: 'center', gap: 6,
            transition: 'background 0.15s'
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.1)'}
        onMouseLeave={e => e.currentTarget.style.background = selected ? 'rgba(59,130,246,0.15)' : 'transparent'}
    >
        {selected && <span>✓</span>}
        {children}
    </div>
);

const Divider = () => <div style={{ height: 1, background: 'var(--border-color, #334155)', margin: '4px 0' }} />;

/* ─────────────────────────────────────────────── */

const ProductTable = ({
    products,
    onOpenActions,
    onOpenLotes,
    isBotica = false,
    isJuguteria = false,
    minimalView = false,
    sortField, sortDir, onSort,
    categoryFilter, onCategoryFilter, uniqueCategories,
    unitFilter, onUnitFilter, uniqueUnits,
    statusFilter, onStatusFilter,
    onClearFilters
}) => {

    const getStockColor = (stock, minStock) => {
        if (stock === 0) return '#e74c3c';
        if (stock <= minStock) return '#f39c12';
        return '#10b981';
    };

    const getStockBadge = (stock, minStock) => {
        if (stock === 0) return <span className="stock-badge depleted">⚠️ AGOTADO</span>;
        if (stock <= minStock) return <span className="stock-badge low">⚠️ BAJO</span>;
        return null;
    };

    const hasActiveFilters = sortField || categoryFilter || unitFilter || statusFilter;

    return (
        <div style={{ overflowX: 'auto' }}>
            {/* Indicador de filtros activos */}
            {hasActiveFilters && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px', marginBottom: 8, flexWrap: 'wrap',
                    background: 'rgba(59,130,246,0.07)',
                    border: '1px solid rgba(59,130,246,0.18)',
                    borderRadius: 10,
                    animation: 'fadeInDown 0.2s ease'
                }}>
                    <span style={{
                        fontSize: '0.75rem', fontWeight: 600, color: '#64748b',
                        textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 2
                    }}>
                        🔍 Filtros:
                    </span>

                    {sortField && (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: '#2563eb', color: '#fff',
                            borderRadius: 20, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600,
                            boxShadow: '0 1px 4px rgba(37,99,235,0.3)'
                        }}>
                            ↕ {sortField === 'name' ? 'Nombre' : sortField === 'sale_price' ? 'P. Venta' : sortField === 'stock' ? 'Stock' : sortField === 'category' ? 'Categoría' : sortField === 'unit' ? 'Unidad' : 'Estado'} {sortDir === 'asc' ? '▴' : '▾'}
                        </span>
                    )}
                    {categoryFilter && (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: '#059669', color: '#fff',
                            borderRadius: 20, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600,
                            boxShadow: '0 1px 4px rgba(5,150,105,0.3)'
                        }}>
                            🏷 {categoryFilter}
                        </span>
                    )}
                    {unitFilter && (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: '#7c3aed', color: '#fff',
                            borderRadius: 20, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600,
                            boxShadow: '0 1px 4px rgba(124,58,237,0.3)'
                        }}>
                            📦 {unitFilter}
                        </span>
                    )}
                    {statusFilter && (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: '#d97706', color: '#fff',
                            borderRadius: 20, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600,
                            boxShadow: '0 1px 4px rgba(217,119,6,0.3)'
                        }}>
                            {statusFilter === 'active' ? '✅' : '❌'} {statusFilter === 'active' ? 'Activo' : 'Inactivo'}
                        </span>
                    )}

                    <button
                        onClick={onClearFilters}
                        style={{
                            marginLeft: 'auto',
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: '#dc2626',
                            border: 'none',
                            color: '#fff',
                            borderRadius: 20, padding: '5px 14px', fontSize: '0.78rem',
                            fontWeight: 700, cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(220,38,38,0.35)',
                            transition: 'all 0.2s'
                        }}
                    >
                        ✕ Limpiar filtros
                    </button>
                </div>
            )}

            <table className="pt-table">
                <thead>
                    <tr>
                        <th className="pt-th">Código</th>
                        
                        <ColMenu label={isJuguteria ? "Armable / Set" : isBotica ? "Medicamento" : "Producto"} field="name" sortField={sortField} sortDir={sortDir} onSort={onSort} active={sortField === 'name'} toggleMode>
                            <MenuItem onClick={() => onSort('name', 'asc')} selected={sortField === 'name' && sortDir === 'asc'}>🔤 A → Z</MenuItem>
                            <MenuItem onClick={() => onSort('name', 'desc')} selected={sortField === 'name' && sortDir === 'desc'}>🔤 Z → A</MenuItem>
                        </ColMenu>

                        {isBotica && <th className="pt-th">Presentación</th>}
                        {isJuguteria && <th className="pt-th">Detalles del Armable</th>}

                        <ColMenu label="Categoría" field="category" sortField={sortField} sortDir={sortDir} onSort={onSort} active={sortField === 'category' || !!categoryFilter}>
                            <MenuItem onClick={() => onSort('category', 'asc')} selected={sortField === 'category' && sortDir === 'asc'}>🔤 A → Z</MenuItem>
                            <MenuItem onClick={() => onSort('category', 'desc')} selected={sortField === 'category' && sortDir === 'desc'}>🔤 Z → A</MenuItem>
                            <Divider />
                            <div style={{ padding: '4px 10px', fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>FILTRAR POR CATEGORÍA</div>
                            <MenuItem onClick={() => onCategoryFilter('')} selected={categoryFilter === ''}>Todas las categorías</MenuItem>
                            {uniqueCategories.map(cat => (
                                <MenuItem key={cat} onClick={() => onCategoryFilter(cat)} selected={categoryFilter === cat}>
                                    {cat}
                                </MenuItem>
                            ))}
                        </ColMenu>

                        <ColMenu label="P. Venta" field="sale_price" sortField={sortField} sortDir={sortDir} onSort={onSort} active={sortField === 'sale_price'} toggleMode>
                            <MenuItem onClick={() => onSort('sale_price', 'asc')} selected={sortField === 'sale_price' && sortDir === 'asc'}>💲 Menor → Mayor</MenuItem>
                            <MenuItem onClick={() => onSort('sale_price', 'desc')} selected={sortField === 'sale_price' && sortDir === 'desc'}>💲 Mayor → Menor</MenuItem>
                        </ColMenu>

                        <ColMenu label={isBotica ? "Stock & Lotes" : "Stock"} field="stock" sortField={sortField} sortDir={sortDir} onSort={onSort} active={sortField === 'stock'} toggleMode>
                            <MenuItem onClick={() => onSort('stock', 'asc')} selected={sortField === 'stock' && sortDir === 'asc'}>📦 Menor → Mayor</MenuItem>
                            <MenuItem onClick={() => onSort('stock', 'desc')} selected={sortField === 'stock' && sortDir === 'desc'}>📦 Mayor → Menor</MenuItem>
                        </ColMenu>

                        <ColMenu label="Estado" field="is_active" sortField={sortField} sortDir={sortDir} onSort={onSort} active={sortField === 'is_active'} toggleMode />

                        <th className="pt-th" style={{ textAlign: 'center' }}>
                            Acciones
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {products.map((product) => (
                        <tr key={product.id} className="pt-tr">
                            <td className="pt-td pt-td-code">{product.code}</td>
                            <td className="pt-td">
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <strong style={{ color: '#0f172a' }}>{product.name}</strong>
                                    {product.unit && (
                                        <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                                            Unidad: {product.unit}
                                        </small>
                                    )}
                                </div>
                            </td>
                            {isBotica && (
                                <td className="pt-td">
                                    {product.presentacion ? (
                                        <span style={{
                                            display: 'inline-block',
                                            background: '#ecfdf5',
                                            color: '#047857',
                                            padding: '3px 8px',
                                            borderRadius: '6px',
                                            fontSize: '0.78rem',
                                            fontWeight: 600,
                                            border: '1px solid #a7f3d0'
                                        }}>
                                            🧴 {product.presentacion}
                                        </span>
                                    ) : (
                                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>
                                    )}
                                </td>
                            )}
                            {isJuguteria && (
                                <td className="pt-td">
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {product.numero_piezas != null && product.numero_piezas !== '' ? (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                                background: '#f1f5f9', color: '#1e293b',
                                                padding: '2px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, width: 'fit-content'
                                            }}>
                                                🧩 {product.numero_piezas} piezas
                                            </span>
                                        ) : null}
                                        {product.nivel_dificultad ? (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                                background: product.nivel_dificultad === 'principiante' ? '#ecfdf5' : product.nivel_dificultad === 'medio' ? '#fefce8' : '#fef2f2',
                                                color: product.nivel_dificultad === 'principiante' ? '#047857' : product.nivel_dificultad === 'medio' ? '#b45309' : '#b91c1c',
                                                border: `1px solid ${product.nivel_dificultad === 'principiante' ? '#a7f3d0' : product.nivel_dificultad === 'medio' ? '#fde68a' : '#fecaca'}`,
                                                padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize', width: 'fit-content'
                                            }}>
                                                {product.nivel_dificultad === 'principiante' ? '🟢 Principiante' : product.nivel_dificultad === 'medio' ? '🟡 Medio' : '🔴 Avanzado'}
                                            </span>
                                        ) : null}
                                        {product.material ? (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                                background: 'rgba(99,102,241,0.08)', color: '#4338ca',
                                                border: '1px solid rgba(99,102,241,0.25)',
                                                padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, width: 'fit-content'
                                            }}>
                                                🪵 {product.material}
                                            </span>
                                        ) : null}
                                        {!product.numero_piezas && !product.nivel_dificultad && !product.material && (
                                            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>
                                        )}
                                    </div>
                                </td>
                            )}
                            <td className="pt-td">
                                <span className={`category-badge ${product.category ? 'has-category' : 'no-category'}`}>
                                    {product.category || 'Sin categoría'}
                                </span>
                            </td>
                            <td className="pt-td">
                                {product.stock <= 0 ? (
                                    <span
                                        className="price-agotado"
                                        title="Sin precio visible — producto agotado"
                                    >
                                        —
                                    </span>
                                ) : (
                                    <span className="price-display">S/. {parseFloat(product.sale_price).toFixed(2)}</span>
                                )}
                            </td>
                            <td className="pt-td">
                                <div className="stock-display" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <div>
                                        <span className="stock-value" style={{ color: getStockColor(product.stock, product.min_stock) }}>
                                            {product.unit === 'kg' ? parseFloat(product.stock).toFixed(2) : Math.floor(product.stock)}
                                        </span>
                                        <span className="stock-min"> / {product.unit === 'kg' ? parseFloat(product.min_stock).toFixed(2) : Math.floor(product.min_stock)}</span>
                                        {getStockBadge(product.stock, product.min_stock)}
                                    </div>
                                    
                                    {/* Botón directo de Lotes con Semáforo (Solo Boticas) */}
                                    {isBotica && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onOpenLotes) onOpenLotes(product);
                                            }}
                                            title="Ver desglose de lotes y fechas de vencimiento (FEFO)"
                                            style={{
                                                background: '#eff6ff',
                                                border: '1px solid #bfdbfe',
                                                color: '#1d4ed8',
                                                borderRadius: '6px',
                                                padding: '3px 8px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                transition: 'all 0.15s'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
                                            onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}
                                        >
                                            💊 Lotes
                                        </button>
                                    )}
                                </div>
                            </td>
                            <td className="pt-td">
                                <span className={`status-badge ${product.is_active ? 'active' : 'inactive'}`}>
                                    {product.is_active ? '✓ ACTIVO' : '✕ INACTIVO'}
                                </span>
                            </td>
                            <td className="pt-td" style={{ textAlign: 'center' }}>
                                <div className="action-menu-wrapper">
                                    <button
                                        className="kebab-btn"
                                        onClick={(e) => { e.stopPropagation(); onOpenActions(product); }}
                                    >⋮</button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default ProductTable;